import React from 'react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// List of void HTML elements that cannot have children
const VOID_ELEMENTS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr'
]);

// Check if an element is a void element
const isVoidElement = (type: any): boolean => {
    if (typeof type === 'string') {
        return VOID_ELEMENTS.has(type.toLowerCase());
    }
    return false;
};

type TransProps = {
    i18nKey: string;
    values?: Record<string, any>;
    components?: Record<string | number, React.ReactElement> | React.ReactElement[];
};

/**
 * Custom Trans component that adapts next-translate patterns to next-intl
 *
 * @param i18nKey - Translation key, optionally with namespace prefix
 * @param values - Values for interpolation
 * @param components - Components for rich text translation
 */
export const Trans: React.FC<TransProps> = ({ i18nKey, values = {}, components = {} }) => {
    const { t } = useTranslation();

    try {
        // Convert components to the format expected by next-intl
        const richParams: Record<string, any> = { ...values };

        // Handle array format (next-translate style) or object format
        if (Array.isArray(components)) {
            components.forEach((component, index) => {
                richParams[index] = (chunks: React.ReactNode) => {
                    // Check if component is a void element (like br, img, input, etc.)
                    const element = component as React.ReactElement;
                    if (isVoidElement(element.type)) {
                        return element;
                    }
                    return React.cloneElement(element, {}, chunks);
                };
            });
        } else if (components && typeof components === 'object') {
            Object.entries(components).forEach(([key, component]) => {
                richParams[key] = (chunks: React.ReactNode) => {
                    // Check if component is a void element (like br, img, input, etc.)
                    const element = component as React.ReactElement;
                    if (isVoidElement(element.type)) {
                        return element;
                    }
                    return React.cloneElement(element, {}, chunks);
                };
            });
        }

        // Use next-intl's rich text translation
        return t.rich(i18nKey, richParams) as React.ReactElement;
    } catch (error) {
        // Fallback to regular translation if rich translation fails
        console.warn(`Trans component fallback for key: ${i18nKey}`, error);
        try {
            return <span>{t(i18nKey, values)}</span>;
        } catch (fallbackError) {
            // Final fallback - return the key itself
            console.error(`Translation failed for key: ${i18nKey}`, fallbackError);
            return <span>{i18nKey}</span>;
        }
    }
};
